import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import {
  KeyCombo,
  ShortcutRegistration,
  ShortcutScope,
  useKeyboardShortcuts,
} from '../hooks/useKeyboardShortcuts';

// Context interface
interface KeyboardShortcutsContextType {
  // Shortcut registration and management
  registerShortcut: (shortcut: ShortcutRegistration) => () => void;
  unregisterShortcut: (id: string) => void;
  enableShortcut: (id: string) => void;
  disableShortcut: (id: string) => void;
  getShortcuts: () => ShortcutRegistration[];
  getShortcutsForScope: (scope: ShortcutScope) => ShortcutRegistration[];

  // Scope management
  activeScope: ShortcutScope;
  setActiveScope: (scope: ShortcutScope) => void;
  pushScope: (scope: ShortcutScope) => void;
  popScope: () => void;

  // Helper methods for creating shortcuts
  createShortcut: (
    id: string,
    combo: KeyCombo,
    handler: (event: KeyboardEvent) => void,
    options: Partial<Omit<ShortcutRegistration, 'id' | 'combo' | 'handler'>>
  ) => ShortcutRegistration;
}

// Create context with a default value
export const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextType | null>(null);

// Provider props
interface KeyboardShortcutsProviderProps {
  children: ReactNode;
  initialScope?: ShortcutScope;
  initialShortcuts?: ShortcutRegistration[];
}

// Provider component
export const KeyboardShortcutsProvider: React.FC<KeyboardShortcutsProviderProps> = ({
  children,
  initialScope = 'global',
  initialShortcuts = [],
}) => {
  // Track registered shortcuts
  const [shortcuts, setShortcuts] = useState<ShortcutRegistration[]>(initialShortcuts);

  // Track scope stack for nested components
  const [scopeStack, setScopeStack] = useState<ShortcutScope[]>([initialScope]);

  // Get current active scope
  const activeScope = useMemo(() => {
    return scopeStack[scopeStack.length - 1];
  }, [scopeStack]);

  // Use the hook for actual shortcut management
  const {
    addShortcut,
    removeShortcut,
    enableShortcut,
    disableShortcut,
    getShortcuts,
    getShortcutsForScope,
  } = useKeyboardShortcuts(shortcuts, activeScope);

  // Register a shortcut
  const registerShortcut = useCallback(
    (shortcut: ShortcutRegistration) => {
      // Check if shortcut already exists to prevent duplicates
      setShortcuts(prevShortcuts => {
        const exists = prevShortcuts.some(s => s.id === shortcut.id);
        if (exists) {
          return prevShortcuts;
        }
        return [...prevShortcuts, shortcut];
      });

      // Add the shortcut using the hook
      const removeShortcutFn = addShortcut(shortcut);

      // Return a cleanup function
      return () => {
        removeShortcutFn();
        setShortcuts(prevShortcuts => prevShortcuts.filter(s => s.id !== shortcut.id));
      };
    },
    [addShortcut]
  );

  // Unregister a shortcut
  const unregisterShortcut = useCallback(
    (id: string) => {
      removeShortcut(id);
      setShortcuts(prevShortcuts => prevShortcuts.filter(s => s.id !== id));
    },
    [removeShortcut]
  );

  // Set active scope directly
  const setActiveScope = useCallback((scope: ShortcutScope) => {
    setScopeStack([scope]);
  }, []);

  // Push a new scope onto the stack
  const pushScope = useCallback((scope: ShortcutScope) => {
    setScopeStack(prevStack => [...prevStack, scope]);
  }, []);

  // Pop the most recent scope
  const popScope = useCallback(() => {
    setScopeStack(prevStack => {
      // Always keep at least one scope
      if (prevStack.length <= 1) {
        return prevStack;
      }
      return prevStack.slice(0, -1);
    });
  }, []);

  // Helper to create shortcuts with less boilerplate
  const createShortcut = useCallback(
    (
      id: string,
      combo: KeyCombo,
      handler: (event: KeyboardEvent) => void,
      options: Partial<Omit<ShortcutRegistration, 'id' | 'combo' | 'handler'>> = {}
    ): ShortcutRegistration => {
      return {
        id,
        combo,
        handler,
        description: options.description || `Shortcut for ${combo}`,
        scope: options.scope || 'global',
        disabled: options.disabled || false,
        preventDefault: options.preventDefault !== false,
        stopPropagation: options.stopPropagation !== false,
        allowInInputs: options.allowInInputs || false,
        priority: options.priority || 0,
      };
    },
    []
  );

  // Create context value
  const contextValue = useMemo(
    () => ({
      registerShortcut,
      unregisterShortcut,
      enableShortcut,
      disableShortcut,
      getShortcuts,
      getShortcutsForScope,
      activeScope,
      setActiveScope,
      pushScope,
      popScope,
      createShortcut,
    }),
    [
      registerShortcut,
      unregisterShortcut,
      enableShortcut,
      disableShortcut,
      getShortcuts,
      getShortcutsForScope,
      activeScope,
      setActiveScope,
      pushScope,
      popScope,
      createShortcut,
    ]
  );

  return (
    <KeyboardShortcutsContext.Provider value={contextValue}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
};

// Custom hook to use the keyboard shortcuts context
export const useKeyboardShortcutsContext = () => {
  const context = useContext(KeyboardShortcutsContext);

  if (!context) {
    throw new Error('useKeyboardShortcutsContext must be used within a KeyboardShortcutsProvider');
  }

  return context;
};

// Higher-order component to provide a scope
export const withKeyboardScope = (Component: React.ComponentType<any>, scope: ShortcutScope) => {
  return (props: any) => {
    const { pushScope, popScope } = useKeyboardShortcutsContext();

    React.useEffect(() => {
      pushScope(scope);
      return () => popScope();
    }, [pushScope, popScope]);

    return <Component {...props} />;
  };
};

// Component that changes the active scope for its children
export const KeyboardScope: React.FC<{
  scope: ShortcutScope;
  children: ReactNode;
}> = ({ scope, children }) => {
  const { pushScope, popScope } = useKeyboardShortcutsContext();

  React.useEffect(() => {
    pushScope(scope);
    return () => popScope();
  }, [pushScope, popScope, scope]);

  return <>{children}</>;
};

// Hook to register a shortcut for the component lifecycle
export const useRegisterShortcut = (shortcut: ShortcutRegistration) => {
  const { registerShortcut } = useKeyboardShortcutsContext();

  React.useEffect(() => {
    const cleanup = registerShortcut(shortcut);
    return cleanup;
  }, [registerShortcut, shortcut]);
};

// Hook to register multiple shortcuts
export const useRegisterShortcuts = (shortcuts: ShortcutRegistration[]) => {
  const { registerShortcut } = useKeyboardShortcutsContext();

  React.useEffect(() => {
    const cleanupFunctions = shortcuts.map(shortcut => registerShortcut(shortcut));

    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [registerShortcut, shortcuts]);
};

export default KeyboardShortcutsContext;
